import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Coins, Info, MessageCircle, Send, Sparkles, Trophy, Zap } from 'lucide-react';
import { formatEuro, TIER_NAME_COLORS, TIER_AVATAR_RING } from './liveTheme';

const tabsFor = (lang) => ([
  { id: 'play', label: 'Chat', icon: MessageCircle },
  { id: 'game', label: lang === 'es' ? 'Juego' : 'Game', icon: Sparkles },
  { id: 'support', label: 'Support', icon: Coins },
  { id: 'ranking', label: 'Ranking', icon: Trophy },
  { id: 'info', label: 'Info', icon: Info },
]);

export const LiveAudienceRail = ({
  lang, token, activeSession, chatMessage, setChatMessage, eventAnswer, setEventAnswer,
  isHost, hostPrompt, setHostPrompt, hostCorrectAnswer, setHostCorrectAnswer,
  onSendChat, onSendTip, onRespond, onLaunch, onClear,
}) => {
  const [activeTab, setActiveTab] = useState('play');
  const [supportMessage, setSupportMessage] = useState('');
  const tabs = useMemo(() => tabsFor(lang), [lang]);
  const activeEvent = activeSession?.active_event;
  const chatRef = useRef(null);
  const messages = activeSession?.chat || [];

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages.length]);

  const tabPanel = () => {
    if (activeTab === 'game') {
      if (activeEvent && activeEvent.status === 'active') {
        return (
          <div className="space-y-3">
            <div className="rounded-xl bg-[#1a1a1a] border border-white/8 p-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <h3 className="text-base font-bold text-white">{activeEvent.title}</h3>
                <span className="rounded-full bg-[#d4a853]/10 border border-[#d4a853]/20 px-2.5 py-0.5 text-[9px] text-[#d4a853] uppercase tracking-wider">{activeEvent.event_type?.replace(/_/g, ' ')}</span>
              </div>
              <p data-testid="pulse-live-active-event-prompt" className="text-sm text-white/50">{activeEvent.prompt}</p>
              <p className="text-[10px] text-white/20 mt-2">{(activeEvent.responses || []).length} {lang === 'es' ? 'respuestas' : 'responses'}</p>
              {isHost && <button data-testid="pulse-live-clear-event" onClick={onClear} className="mt-3 rounded-full bg-white/5 border border-white/10 px-4 py-2 text-sm text-white/50 hover:text-white transition">{lang === 'es' ? 'Cerrar evento' : 'Close event'}</button>}
            </div>
          </div>
        );
      }
      if (isHost) {
        return (
          <div className="space-y-3">
            <input data-testid="pulse-live-host-prompt" value={hostPrompt} onChange={(e) => setHostPrompt(e.target.value)} placeholder={lang === 'es' ? 'Prompt personalizado...' : 'Custom prompt...'} className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-white/20" />
            <input data-testid="pulse-live-host-answer" value={hostCorrectAnswer} onChange={(e) => setHostCorrectAnswer(e.target.value)} placeholder={lang === 'es' ? 'Respuesta correcta (trivia)' : 'Correct answer (trivia)'} className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-white/20" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <button data-testid="pulse-live-launch-countdown" onClick={() => onLaunch('countdown')} className="rounded-xl bg-white text-black px-3 py-2.5 text-sm font-semibold">Countdown</button>
              <button data-testid="pulse-live-launch-poll" onClick={() => onLaunch('poll_yes_no')} className="rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-white hover:bg-white/10 transition">Poll</button>
              <button data-testid="pulse-live-launch-word-drop" onClick={() => onLaunch('word_drop')} className="rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-white hover:bg-white/10 transition">Word Drop</button>
              <button data-testid="pulse-live-launch-trivia" onClick={() => onLaunch('trivia_music')} className="rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-white hover:bg-white/10 transition">Trivia</button>
            </div>
          </div>
        );
      }
      return <p className="text-sm text-white/20 py-4">{lang === 'es' ? 'El host aun no ha lanzado nada.' : "Host hasn't launched anything yet."}</p>;
    }

    if (activeTab === 'support') {
      return (
        <div className="space-y-3">
          {token ? (
            <>
              <div className="grid grid-cols-3 gap-2">
                {[2, 5, 10].map((a) => (
                  <button key={a} data-testid={`pulse-live-tip-${a}`} onClick={() => onSendTip(a, supportMessage)} className="rounded-xl bg-[#d4a853]/8 border border-[#d4a853]/15 px-3 py-3 text-sm font-semibold text-[#d4a853] hover:bg-[#d4a853]/15 transition">{a}{'\u20AC'}</button>
                ))}
              </div>
              <input data-testid="pulse-live-tip-message" value={supportMessage} onChange={(e) => setSupportMessage(e.target.value)} placeholder={lang === 'es' ? 'Mensaje...' : 'Message...'} className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-white/20" />
              <p className="text-[10px] text-white/15">{lang === 'es' ? '30% gestion Pluka' : '30% Pluka management'}</p>
            </>
          ) : <p className="text-sm text-white/20">{lang === 'es' ? 'Inicia sesion para apoyar.' : 'Sign in to support.'}</p>}
        </div>
      );
    }

    if (activeTab === 'ranking') {
      const board = activeSession.scoreboard || [];
      return (
        <div className="space-y-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-white/30">Top</span>
            <span className="flex items-center gap-1 text-[11px] text-white/20"><Zap size={11} /> {activeSession.social_power || 0}</span>
          </div>
          {board.length > 0 && (
            <div className="flex items-end justify-center gap-3 mb-4 pt-2">
              {[board[1], board[0], board[2]].filter(Boolean).map((entry, i) => {
                const pos = [2, 1, 3][i];
                const heights = ['h-12', 'h-16', 'h-10'];
                const colors = ['text-white/50', 'text-[#d4a853]', 'text-white/40'];
                return (
                  <div key={entry.user_id} className="flex flex-col items-center">
                    <span className={`text-[10px] font-bold ${colors[i]} mb-1`}>#{pos}</span>
                    <div className={`w-10 ${heights[i]} rounded-t-lg ${pos === 1 ? 'bg-[#d4a853]/15 border border-[#d4a853]/20' : 'bg-white/5 border border-white/8'} flex items-center justify-center`}>
                      <span className="text-[10px] text-white/50">{entry.score}</span>
                    </div>
                    <span className="text-[9px] text-white/30 mt-1 max-w-[60px] truncate">{entry.user_name}</span>
                  </div>
                );
              })}
            </div>
          )}
          {board.slice(3, 8).map((entry, i) => (
            <div key={entry.user_id} className="rounded-xl bg-white/4 border border-white/6 px-3 py-2 flex items-center justify-between">
              <span className="text-[12px] text-white/50">#{i + 4} {entry.user_name}</span>
              <span className="text-[10px] text-white/20 tabular-nums">{entry.score}</span>
            </div>
          ))}
          {!board.length && <p className="text-sm text-white/20 py-2">{lang === 'es' ? 'Sin ranking.' : 'No ranking yet.'}</p>}
        </div>
      );
    }

    if (activeTab === 'info') {
      return (
        <div className="space-y-3">
          <h3 className="text-base font-semibold text-white">{activeSession.artist_name}</h3>
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-xl bg-white/4 border border-white/6 px-3 py-3">
              <p className="text-[9px] text-white/20 uppercase tracking-wider">{lang === 'es' ? 'Formato' : 'Format'}</p>
              <p className="text-sm text-white mt-1">{activeSession.session_format === 'show' ? 'Show' : 'Room'}</p>
            </div>
            <div className="rounded-xl bg-white/4 border border-white/6 px-3 py-3">
              <p className="text-[9px] text-white/20 uppercase tracking-wider">Support</p>
              <p className="text-sm text-white mt-1">{formatEuro(activeSession.total_donations || 0)}</p>
            </div>
            <div className="rounded-xl bg-white/4 border border-white/6 px-3 py-3">
              <p className="text-[9px] text-white/20 uppercase tracking-wider">Tier</p>
              <p className="text-sm text-white mt-1">{activeSession.presence_tier || 'base'}</p>
            </div>
          </div>
          {activeSession.description && <p className="text-sm text-white/30 leading-relaxed">{activeSession.description}</p>}
          <p className="text-[10px] text-white/15">{lang === 'es' ? 'Comision plataforma: 30%' : 'Platform commission: 30%'}</p>
        </div>
      );
    }

    // play/chat tab — stats
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-xl bg-white/4 border border-white/6 px-3 py-2.5 text-center">
            <p className="text-[9px] text-white/20 uppercase">Viewers</p>
            <p className="text-sm font-semibold text-white mt-1">{activeSession.viewers || 0}</p>
          </div>
          <div className="rounded-xl bg-white/4 border border-white/6 px-3 py-2.5 text-center">
            <p className="text-[9px] text-white/20 uppercase">Chat</p>
            <p className="text-sm font-semibold text-white mt-1">{activeSession.engagement?.chat_messages || 0}</p>
          </div>
          <div className="rounded-xl bg-white/4 border border-white/6 px-3 py-2.5 text-center">
            <p className="text-[9px] text-white/20 uppercase">Support</p>
            <p className="text-sm font-semibold text-white mt-1">{formatEuro(activeSession.total_donations || 0)}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="pulse-live-deck" className="grid xl:grid-cols-[1.2fr_0.8fr] gap-4">
      <div className="rounded-2xl bg-[#111] border border-white/8 p-4">
        <div className="flex gap-1 mb-4 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button key={tab.id} data-testid={`pulse-live-tab-${tab.id}`} onClick={() => setActiveTab(tab.id)} className={`rounded-full px-3 py-1.5 text-[12px] font-medium transition flex items-center gap-1.5 whitespace-nowrap ${active ? 'bg-white text-black' : 'text-white/35 hover:text-white/55'}`}>
                <Icon size={13} /> {tab.label}
              </button>
            );
          })}
        </div>
        {tabPanel()}
      </div>

      {/* Enhanced chat stream */}
      <div data-testid="pulse-live-chat-rail" className="rounded-2xl bg-[#111] border border-white/8 p-3 flex flex-col">
        <div ref={chatRef} className="flex-1 space-y-1.5 max-h-[400px] overflow-y-auto pr-1 scroll-smooth">
          {messages.slice(-30).map((msg) => {
            const isTip = msg.type === 'tip' || msg.type === 'gift';
            const isGift = msg.type === 'gift';
            const initial = (msg.user_name || '?')[0].toUpperCase();
            const tier = msg.presence_tier || 'base';

            if (isTip) {
              const giftIcon = isGift ? (msg.amount >= 10 ? '\uD83D\uDC51' : msg.amount >= 5 ? '\uD83D\uDC8E' : '\u2B50') : '';
              return (
                <div key={msg.id} data-testid={`pulse-live-chat-tip-${msg.id}`} className={`rounded-xl ${isGift ? 'bg-[#d4a853]/12 border border-[#d4a853]/20' : 'bg-[#d4a853]/8 border border-[#d4a853]/12'} px-3 py-2 flex items-center gap-2`} style={{ animation: 'fadeIn .3s ease' }}>
                  <span className="text-sm shrink-0">{giftIcon || '\uD83D\uDCB0'}</span>
                  <div className="min-w-0">
                    <span className="text-[10px] font-semibold text-[#d4a853]">{msg.user_name}</span>
                    <span className="text-[10px] text-[#d4a853]/50 ml-1.5">{msg.message}</span>
                  </div>
                </div>
              );
            }

            return (
              <div key={msg.id} data-testid={`pulse-live-chat-message-${msg.id}`} className="flex items-start gap-2">
                <div className={`w-5 h-5 shrink-0 rounded-full border ${TIER_AVATAR_RING[tier] || TIER_AVATAR_RING.base} bg-white/5 flex items-center justify-center text-[8px] font-bold text-white/40`}>{initial}</div>
                <div className="min-w-0">
                  <span className={`text-[10px] font-medium ${TIER_NAME_COLORS[tier] || TIER_NAME_COLORS.base}`}>{msg.user_name}</span>
                  <p className="text-[12px] text-white/45 break-words leading-relaxed">{msg.message}</p>
                </div>
              </div>
            );
          })}
        </div>
        {token ? (
          <div className="mt-2 flex gap-2">
            <input data-testid="pulse-live-chat-input" value={chatMessage} onChange={(e) => setChatMessage(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && onSendChat()} placeholder={lang === 'es' ? 'Escribe...' : 'Type...'} className="flex-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-[12px] text-white placeholder:text-white/20 focus:outline-none focus:border-white/20" />
            <button data-testid="pulse-live-chat-send" onClick={onSendChat} className="rounded-full bg-white text-black p-2"><Send size={13} /></button>
          </div>
        ) : <p className="text-[10px] text-white/15 mt-2">{lang === 'es' ? 'Inicia sesion para chatear.' : 'Sign in to chat.'}</p>}
        <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}}`}</style>
      </div>
    </div>
  );
};
